import { DEFAULT_DEBOUNCE_DELAY, STATUSPAGE } from '@repo/ui';
import debounce from 'debounce';
import { notifyError } from 'presentation/app/components/notify';
import { useService } from 'presentation/app/hooks/useService';
import {
  IPerformanceReportForProfessionalsFilters,
  PerformanceReportForProfessionalsQueryParams,
  PerformanceReportForProfessionalsRankingOrderColumn,
  PerformanceReportForProfessionalsRankingOrderDirection,
} from 'presentation/app/modules/Reports/models/performance-report-for-professionals';
import { useAppSelector } from 'presentation/config/hooks/useRedux';
import {
  loadPerformanceReportForProfessionalsOrder,
  loadPerformanceReportForProfessionalsTotal,
  loadPerformanceReportForProfessionalsSearch,
  loadPerformanceReportForProfessionalsSummary,
  loadPerformanceReportForProfessionalsFilters,
  loadPerformanceReportForProfessionalsEvolution,
  loadPerformanceReportForProfessionalsRowsPerPage,
  loadPerformanceReportForProfessionalsCurrentPage,
  loadPerformanceReportForProfessionalsDetailedList,
  loadPerformanceReportForProfessionalsTopValidatedActivities,
} from 'presentation/config/store/reports/performance-report-for-professionals-slice';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

export const usePerformanceReportForProfessionals = () => {
  const { reportService } = useService();

  // redux
  const dispatch = useDispatch();

  // states
  const [hasFetchedData, setHasFetchedData] = useState(false);
  const [statusGetDashboard, setStatusGetDashboard] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );
  const [statusGetRanking, setStatusGetRanking] = useState<STATUSPAGE>(
    STATUSPAGE.DONE,
  );
  const [statusExport, setStatusExport] = useState<STATUSPAGE>(STATUSPAGE.DONE);

  const {
    total,
    search,
    filters,
    summary,
    evolution,
    rowsPerPage,
    orderColumn,
    currentPage,
    detailedList,
    orderDirection,
    topValidatedActivities,
  } = useAppSelector((state) => state.performanceReportForProfessionals);

  const saveFilters = (values: IPerformanceReportForProfessionalsFilters) => {
    dispatch(loadPerformanceReportForProfessionalsFilters(values));
  };

  const handleSearchRemote = useCallback(
    (searchString: string) => {
      setStatusGetRanking(STATUSPAGE.LOADING);
      dispatch(loadPerformanceReportForProfessionalsSearch(searchString));
      dispatch(loadPerformanceReportForProfessionalsCurrentPage(0));
    },
    [dispatch],
  );

  const requestGetPerformanceReportForProfessionalsDashboard = async (
    params: PerformanceReportForProfessionalsQueryParams,
  ) => {
    setStatusGetDashboard(STATUSPAGE.LOADING);

    const [summaryResponse, topValidatedActivitiesResponse, evolutionResponse] =
      await Promise.allSettled([
        reportService.getPerformanceReportForProfessionalsSummary(params),
        reportService.getPerformanceReportForProfessionalsTopValidatedActivities(
          params,
        ),
        reportService.getPerformanceReportForProfessionalsEvolution(params),
      ]);

    if (summaryResponse.status === 'fulfilled') {
      dispatch(
        loadPerformanceReportForProfessionalsSummary(summaryResponse.value),
      );
    } else {
      dispatch(loadPerformanceReportForProfessionalsSummary(null));
      notifyError(
        summaryResponse.reason?.message ||
          'Não foi possível carregar os indicadores.',
      );
    }

    if (topValidatedActivitiesResponse.status === 'fulfilled') {
      dispatch(
        loadPerformanceReportForProfessionalsTopValidatedActivities(
          topValidatedActivitiesResponse.value,
        ),
      );
    } else {
      dispatch(loadPerformanceReportForProfessionalsTopValidatedActivities([]));
      notifyError(
        topValidatedActivitiesResponse.reason?.message ||
          'Não foi possível carregar o gráfico Top 10.',
      );
    }

    if (evolutionResponse.status === 'fulfilled') {
      dispatch(
        loadPerformanceReportForProfessionalsEvolution(evolutionResponse.value),
      );
    } else {
      dispatch(loadPerformanceReportForProfessionalsEvolution([]));
      notifyError(
        evolutionResponse.reason?.message ||
          'Não foi possível carregar a evolução das atividades validadas.',
      );
    }

    setStatusGetDashboard(STATUSPAGE.DONE);
  };

  const requestGetPerformanceReportForProfessionalsRanking = useCallback(
    async (params: {
      filters: PerformanceReportForProfessionalsQueryParams;
      page: number;
      length: number;
      orderColumn: PerformanceReportForProfessionalsRankingOrderColumn;
      orderDirection: PerformanceReportForProfessionalsRankingOrderDirection;
      search?: string;
    }) => {
      setStatusGetRanking(STATUSPAGE.LOADING);

      try {
        const response =
          await reportService.getPerformanceReportForProfessionalsRanking({
            ...params.filters,
            page: params.page,
            length: params.length,
            orderColumn: params.orderColumn,
            orderDirection: params.orderDirection,
            search: params.search,
          });

        dispatch(
          loadPerformanceReportForProfessionalsDetailedList(response.data),
        );
        dispatch(loadPerformanceReportForProfessionalsTotal(response.total));
        dispatch(loadPerformanceReportForProfessionalsCurrentPage(params.page));
        dispatch(
          loadPerformanceReportForProfessionalsRowsPerPage(params.length),
        );
        dispatch(
          loadPerformanceReportForProfessionalsOrder({
            orderColumn: params.orderColumn,
            orderDirection: params.orderDirection,
          }),
        );
      } catch (error) {
        dispatch(loadPerformanceReportForProfessionalsDetailedList([]));
        dispatch(loadPerformanceReportForProfessionalsTotal(0));
        notifyError(
          error?.message ||
            'Não foi possível carregar o ranking de desempenho.',
        );
      } finally {
        setStatusGetRanking(STATUSPAGE.DONE);
        setHasFetchedData(true);
      }
    },
    [dispatch, reportService],
  );

  const debouncedRequestGetPerformanceReportForProfessionalsRanking = useMemo(
    () =>
      debounce(
        (params: {
          filters: PerformanceReportForProfessionalsQueryParams;
          page: number;
          length: number;
          orderColumn: PerformanceReportForProfessionalsRankingOrderColumn;
          orderDirection: PerformanceReportForProfessionalsRankingOrderDirection;
          search?: string;
        }) => {
          requestGetPerformanceReportForProfessionalsRanking(params);
        },
        DEFAULT_DEBOUNCE_DELAY,
      ),
    [requestGetPerformanceReportForProfessionalsRanking],
  );

  useEffect(() => {
    return () => {
      debouncedRequestGetPerformanceReportForProfessionalsRanking.clear();
    };
  }, [debouncedRequestGetPerformanceReportForProfessionalsRanking]);

  const requestRefreshAllPerformanceReportForProfessionalsData = async (
    params: PerformanceReportForProfessionalsQueryParams,
  ) => {
    await Promise.allSettled([
      requestGetPerformanceReportForProfessionalsDashboard(params),
      requestGetPerformanceReportForProfessionalsRanking({
        filters: params,
        page: 0,
        length: rowsPerPage,
        orderColumn,
        orderDirection,
        search,
      }),
    ]);
  };

  const requestExportPerformanceReportForProfessionals = async (
    params: PerformanceReportForProfessionalsQueryParams,
  ) => {
    setStatusExport(STATUSPAGE.LOADING);

    try {
      const file =
        await reportService.exportPerformanceReportForProfessionalsExcel(
          params,
        );

      const blob = new Blob([file], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'desempenho-dos-profissionais.xlsx';
      link.click();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      notifyError(
        error?.message || 'Não foi possível exportar o relatório em Excel.',
      );
    } finally {
      setStatusExport(STATUSPAGE.DONE);
    }
  };

  const statusGet =
    statusGetDashboard === STATUSPAGE.LOADING ||
    statusGetRanking === STATUSPAGE.LOADING
      ? STATUSPAGE.LOADING
      : STATUSPAGE.DONE;

  return {
    total,
    search,
    filters,
    summary,
    evolution,
    statusGet,
    statusGetDashboard,
    statusGetRanking,
    currentPage,
    rowsPerPage,
    orderColumn,
    saveFilters,
    detailedList,
    statusExport,
    orderDirection,
    hasFetchedData,
    topValidatedActivities,
    handleSearchRemote,
    debouncedRequestGetPerformanceReportForProfessionalsRanking,
    requestExportPerformanceReportForProfessionals,
    requestGetPerformanceReportForProfessionalsRanking,
    requestRefreshAllPerformanceReportForProfessionalsData,
  };
};
